import { useMemo } from "react";
import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../services";
import { User } from "../../../models";

import { useUserQuery } from "./user.query";
import {
  useUserGlobalActivity,
  useUserGlobalStatus,
} from "./user.globalStatus";

export { useUserGlobalActivity, useUserGlobalStatus };

export * from "./user.invalidate";
export * from "./user.mutations";

export const userAccessError = new Error(
  "Attempted to access user data but it is not available",
);

// ###################
// ## useUserStatus ##
// ###################

export type ResultOfUseUserStatus = QueryStatus;
export type UseUserStatus = () => ResultOfUseUserStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the user data.
 */
export const useUserStatus: UseUserStatus = () => {
  return useUserQuery({ watch: ["status"] }).status;
};

// ###################
// ## useUserError ##
// ###################

export type ResultOfUseUserError = AxiosError | null;
export type UseUserError = () => ResultOfUseUserError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the user data
 * or null if no error occurred.
 */
export const useUserError: UseUserError = () => {
  return useUserQuery({ watch: ["error"] }).error;
};

// ######################
// ## useMaybeUserData ##
// ######################

export type ResultOfUseMaybeUserData = User | undefined;
export type UseMaybeUserData = () => ResultOfUseMaybeUserData;

/**
 * The returned value of this hook will contain
 * the current user data, which may be undefined
 */
export const useMaybeUserData: UseMaybeUserData = () => {
  return useUserQuery({ watch: ["data"] }).data;
};

// #################
// ## useUserData ##
// #################

export type ResultOfUseUserData = User;
export type UseUserData = () => ResultOfUseUserData;

/**
 * The returned value of this hook will contain the current user data.
 *
 * This hook is guaranteed to contain user data or will throw.
 */
export const useUserData: UseUserData = () => {
  const data = useMaybeUserData();
  if (data === undefined) {
    throw userAccessError;
  }
  return data;
};

// ##################
// ## useMaybeUser ##
// ##################

export type ResultOfUseMaybeUser = QueryEnvelope<User>;
export type UseMaybeUser = () => ResultOfUseMaybeUser;

/**
 * The `data` property returned from this hook will contain
 * the current user data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the user data.
 */
export const useMaybeUser: UseMaybeUser = () => {
  const { status, error, data } = useUserQuery({
    watch: ["status", "error", "data"],
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// #############
// ## useUser ##
// #############

export type ResultOfUseUser = EnsuredQueryEnvelope<User>;
export type UseUser = () => ResultOfUseUser;

/**
 * The `data` property returned from this hook will contain
 * the current user data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the user data.
 *
 * This hook is guaranteed to contain user data or will throw.
 */
export const useUser: UseUser = () => {
  const { status, error, data } = useUserQuery({
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw userAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
